import { AggregateDefinition } from '@terragotech/gen5-shared-components';
import { DocumentNode } from 'graphql';
import _, { isEmpty, isEqual, isNil } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkForExistingData,
  createWithAggregateNameObject,
  extractDataFromResult,
  getAssetsFromResults,
} from './aggregateUtils';
import QUERY, { FilterQuery, GlobalSearchQueryCreator, RecordTypeAssetCountQuery, UnavailableDataQuery } from './query';
import {
  AggregatesContextType,
  AssetType,
  GraphQlAssetType,
  MapAssetType,
  pageInfo,
  QueryResultDataType,
  recordData,
} from './types';
import { VariablesRecord, useLazyQueries } from './useLazyQueries';
import { ApolloQueryResult, useApolloClient } from '@apollo/client';
import { pascalCase } from 'change-case';
import { SortDirection } from '@terragotech/react-data-grid';
import { useProjects } from '../projectsContext';
import { getSortKey, getTimeZoneDifference, MAX_ASSET_LIMIT_MULTISELECT, PAGE_SIZE_MULTISELECT } from './queryUtils';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  aiFilterState,
  buildingsState,
  cardIdsState,
  configState,
  drawFilterSelector,
  floorPlansState,
  forceMapReloadState,
  getRecordFilterCallback,
  initialTableLoadState,
  mapBoundsSelector,
  mapExtentFilterEnabledState,
  quickSearchState,
  selectedFloorPlanState,
  selectedRecordTypeFilterSelector,
  selectedRecordTypeState,
  sortSelector,
} from '../../recoil/atoms';
import { useAssetCards } from '../assetCardContext';
import { FilterState } from '../FilterContext/types';
import { NOT_MAP_ASSETS_NAMES } from '@terragotech/gen5-shared-utilities';
import { LanguageContext } from '../LanguageContext/languageContext';
import { point, polygon } from '@turf/helpers';
import inside from '@turf/boolean-point-in-polygon';
import { DrawFeature } from '../../components/Map/component/useDrawFeature';
import { projectIdsSelector, selectedProjectsState } from '../../recoil/atoms/project';
import { BuildingType, FloorPlanType } from './types';
import { BUILDING_DEFAULT_KEYS, FLOOR_PLAN_DEFAULT_KEYS } from '@terragotech/gen5-shared-utilities';

export interface UseAssetsLoaderArgs {
  aggregateDefinitions: AggregateDefinition[];
  mapAggregateDefinitions: AggregateDefinition[];
  visibleAggregateTypesNames: string[];
}

export interface UseAssetsLoaderReturnType {
  refetchAll: () => void;
  isDataLoading: boolean;
  assets: MapAssetType[];
  referenceTypeAssets: MapAssetType[];
  recordTypesData: record[] | undefined;
  fetchNextDataSet: (fetchAfter: boolean) => Promise<number | undefined>;
  updateScrollPosition: (recordType: string, index: number) => void;
  update: (recordType: string[], updatedProject: string) => Promise<void>;
  syncUnavailableData: (sinceTime: number, endTime: number) => Promise<void>;
  deleteAssets: (deletedAssets: Record<string, string[]>) => void;
  deleteAssetsById: (ids: string[]) => void;
  fetchAssetsForFilter: (
    field: string,
    limit?: number,
    offset?: number,
    searchText?: string
  ) => Promise<string[] | undefined>;
  filteredCount: { [key: string]: number };
  getSearchResults: (searchText: string) => Promise<MapAssetType[]>;
  getAssetsForMultiSelect: () => Promise<never[] | AssetType[]>;
  sortAssets: (assets: MapAssetType[], sortKey?: string, sortDirection?: SortDirection) => MapAssetType[];
}

interface queryVariables {
  filters?: Filters;
  conditions?: Conditions;
  first?: number;
  after?: string;
  before?: string;
  orderBy?: string;
  last?: number;
}
type ValueRecord = Record<string, Object | Array<string> | boolean>;
type Conditions = Record<string, string | { [key: string]: string | { [key: string]: string } }>;
type fetchedResult = {
  [key: string]: {
    data: { edges: { node: GraphQlAssetType; cursor: string }[]; pageInfo: pageInfo; totalCount?: number };
  };
};

export type FilterValue =
  | { in: string[] | number[] | boolean[] }
  | { notIn: string[] | number[] }
  | { includesInsensitive: string }
  | { greaterThan: number | string }
  | { lessThan: number | string }
  | { label: { includesInsensitive: string } }
  | { label: { inInsensitive: string[] } }
  | { greaterThanOrEqualTo: string; lessThanOrEqualTo: string }
  | Array<{ [key: string]: { [fieldKey: string]: string } }>
  | { in: string[] | number[]; isNull: boolean }
  | { equalTo: number | string | boolean | null }
  | { notEqualTo: number | string | boolean | null }
  | { greaterThanOrEqualTo: number | string }
  | { lessThanOrEqualTo: number | string }
  | { isNull: boolean }
  | { startsWith: string }
  | { notStartsWith: string }
  | { endsWith: string }
  | { notEndsWith: string }
  | { like: string }
  | { notLike: string }
  | { distinctFrom: number | string | null }
  | { notDistinctFrom: number | string | null }
  | { dateContains: string }
  | {
      label?: {
        includesInsensitive?: string;
        inInsensitive?: string[];
      };
    }
  | { coveredBy: Pick<DrawFeature, 'type' | 'coordinates'> }
  | {
      and?: FilterValue[];
      or?: FilterValue[];
      not?: FilterValue;
    }
  | { [key: string]: FilterValue };

export interface Filters {
  and?: Filters[];
  [key: string]: FilterValue | Filters[] | Filters | undefined;
}

export interface record {
  label: string;
  value: string;
  count: number;
  plural: string;
}

interface recordTypeCountResult {
  data: {
    [key: string]: { totalCount: number };
  };
}

export const getQuery = (
  aggregateDefinition: AggregateDefinition,
  filterState: FilterState = {},
  selectedProjects: string[],
  assetRecord?: AggregatesContextType,
  allProjectIds?: string[],
  aiFilters?: Record<string, string>
) => {
  if (
    (assetRecord && !assetRecord.visibleAggregateTypesNames.includes(aggregateDefinition.name)) ||
    (!!allProjectIds && allProjectIds.length === 0)
  ) {
    return undefined;
  }

  return {
    type: 'AggregateLoader',
    aggregateType: aggregateDefinition.name,
    mandatoryFilter: {
      filter: {
        type: 'GroupFilter',
        conjunction: 'AND',
        condition: [
          ...(aiFilters?.[aggregateDefinition.name]
            ? [
                {
                  type: 'SimpleFilter',
                  key: aiFilters?.[aggregateDefinition.name],
                  operator: 'AI',
                },
              ]
            : []),
          ...((!allProjectIds || selectedProjects.length !== allProjectIds.length) &&
          !aggregateDefinition.isReferenceType
            ? !allProjectIds || selectedProjects.length < allProjectIds.length / 2
              ? [
                  {
                    type: 'SimpleFilter',
                    key: 'project',
                    operator: 'INCLUDE',
                    options: selectedProjects,
                  },
                ]
              : [
                  {
                    type: 'SimpleFilter',
                    key: 'project',
                    operator: 'EXCLUDE',
                    options: allProjectIds.filter(id => !selectedProjects.includes(id)),
                  },
                ]
            : []),
          ...Object.keys(filterState)
            .map(field => {
              const actualField = field.split('-')[0];
              const currType = aggregateDefinition?.propertyDefinitions.find(obj => obj.field === actualField);

              // Date only filtering
              if (currType && (currType.type === 'Date' || currType.type === 'DateTime')) {
                return {
                  type: 'SimpleFilter',
                  key: actualField,
                  operator: 'BETWEEN',
                  lowerBound: new Date(filterState[field][0]),
                  upperBound: new Date(filterState[field][1]),
                };
              }
              if (field.endsWith('-QuickFilter')) {
                return {
                  type: 'SimpleFilter',
                  key: currType && currType.relationshipType === 'ONE_TO_ONE' ? 'label' : actualField,
                  foreignTable:
                    currType && currType.relationshipType === 'ONE_TO_ONE' ? `"ref${actualField}"` : undefined,
                  operator: 'LIKE',
                  options: `%${filterState[field]}%`,
                };
              } else {
                const val = filterState[field]?.not ?? filterState[field];
                return {
                  type: 'SimpleFilter',
                  key: currType && currType.relationshipType === 'ONE_TO_ONE' ? 'label' : actualField,
                  foreignTable:
                    currType && currType.relationshipType === 'ONE_TO_ONE' ? `"ref${actualField}"` : undefined,
                  operator: filterState[field]?.not ? 'EXCLUDE' : 'INCLUDE',
                  options: val?.length ? val : ['INVALIDVALUEDONOTRETURN'],
                };
              }
            })
            .filter(a => a),
        ],
      },
    },
  } as any;
};
const INITIAL_CHUNK_SIZE = 300;
const NEXT_CHUNK_SIZE = 200;

export const useAssetsLoader = (props: UseAssetsLoaderArgs): UseAssetsLoaderReturnType => {
  const { aggregateDefinitions, mapAggregateDefinitions, visibleAggregateTypesNames } = props;
  const { translate, language } = useContext(LanguageContext);
  const [assetStates, setAssetStates] = useState<{ [recordType: string]: recordData }>(
    createWithAggregateNameObject(mapAggregateDefinitions, () => ({}))
  );
  const configContext = useRecoilValue(configState);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const selectedProjects = useRecoilValue(selectedProjectsState);
  const allProjectIds = useRecoilValue(projectIdsSelector);
  const setBuildings = useSetRecoilState(buildingsState);
  const setFloorPlans = useSetRecoilState(floorPlansState);
  const [recordTypesData, setRecordTypesData] = useState<record[]>();
  const { refetch: refetchProjects } = useProjects();
  const [referenceTypeAssets, setReferenceTypeAssets] = useState<MapAssetType[]>([]);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const client = useApolloClient();
  const setForceMapReload = useSetRecoilState(forceMapReloadState);
  const setInitialTableLoadState = useSetRecoilState(initialTableLoadState);
  const mapViewBox = useRecoilValue(mapBoundsSelector);
  const cardIds = useRecoilValue(cardIdsState);
  const { setRefresh } = useAssetCards();
  const filterState = useRecoilValue(selectedRecordTypeFilterSelector);
  const aiFilter = useRecoilValue(aiFilterState);
  const getRecordFilter = useRecoilCallback(getRecordFilterCallback, []);
  const quickSearch = useRecoilValue(quickSearchState);
  const doesRecordHasNextPage = useRef<boolean | undefined>(false);
  const isMapExtentEnabled = useRecoilValue(mapExtentFilterEnabledState);
  const isMapExtentEnabledRef = useRef(isMapExtentEnabled);
  const drawFilter = useRecoilValue(drawFilterSelector);
  const searchKey = useMemo(() => (_.isEmpty(filterState) ? null : filterState), [filterState]);
  const sort = useRecoilValue(sortSelector);
  const sortRef = useRef(sort);
  const selectedRecordType = useRecoilValue(selectedRecordTypeState);
  const selectedRecordTypeRef = useRef(selectedRecordType);
  const selectedRecordTypeDefinition = mapAggregateDefinitions.find(d => d.name === selectedRecordType);
  const selectedRecordTypeDefinitionRef = useRef(selectedRecordTypeDefinition);
  const filterValues = useMemo(() => (searchKey ? Object.keys(searchKey).map(key => searchKey[key]) : []), [searchKey]);
  const filteredSearchKeys = useMemo(() => _.keys(searchKey).map(item => item), [searchKey]);
  const mapViewBoxRef = useRef(mapViewBox);
  const prevQueryKey = useRef('');
  const cardIdsRef = useRef(cardIds);
  const [filteredCount, setFilteredCount] = useState<{ [recordName: string]: number }>({});
  const [assetQueryState, setAssetQueryState] = useState<{
    [key: string]: {
      sortKey: string;
      columnKey: string;
      variables: queryVariables;
    };
  }>({});
  const selectedFloorPlan = useRecoilValue(selectedFloorPlanState);
  const selectedFloorPlanRef = useRef(selectedFloorPlan);

  const selectedRecordTypeAssets = useMemo(
    () => assetStates[selectedRecordTypeDefinition?.queryKey ?? '']?.assetData ?? [],
    [assetStates, selectedRecordTypeDefinition?.queryKey]
  );

  const getProjectsFilter = (selectedProjects: string[], allProjectIds: string[]) => {
    return !allProjectIds || selectedProjects.length < allProjectIds.length / 2
      ? {
          in: selectedProjects,
        }
      : {
          notIn: allProjectIds.filter(id => !selectedProjects.includes(id)),
        };
  };

  const [projectsFilter, setProjectsFilter] = useState(getProjectsFilter(selectedProjects, allProjectIds));

  useEffect(() => {
    const newFilter = getProjectsFilter(selectedProjects, allProjectIds);
    setProjectsFilter(prev =>
      !firstLoad && (allProjectIds.length === 0 || isEqual(prev, newFilter)) ? prev : newFilter
    );
  }, [allProjectIds, selectedProjects]);

  useEffect(() => {
    mapViewBoxRef.current = mapViewBox;
  }, [mapViewBox]);

  useEffect(() => {
    isMapExtentEnabledRef.current = isMapExtentEnabled;
  }, [isMapExtentEnabled]);

  useEffect(() => {
    sortRef.current = sort;
  }, [sort]);

  useEffect(() => {
    selectedRecordTypeRef.current = selectedRecordType;
  }, [selectedRecordType]);

  useEffect(() => {
    selectedRecordTypeDefinitionRef.current = selectedRecordTypeDefinition;
  }, [selectedRecordTypeDefinition]);

  useEffect(() => {
    cardIdsRef.current = cardIds;
  }, [cardIds]);

  useEffect(() => {
    doesRecordHasNextPage.current = assetStates[selectedRecordTypeDefinition?.queryKey || '']?.pageInfo?.hasNextPage;
  }, [assetStates]);

  useEffect(() => {
    selectedFloorPlanRef.current = selectedFloorPlan;
  }, [selectedFloorPlan]);

  const getQueryVariable = (queryVariables?: queryVariables, isReferenceType?: boolean) => {
    return {
      filters:
        queryVariables?.filters ||
        (isReferenceType
          ? { label: { notEqualTo: '' } }
          : {
              projectId: projectsFilter,
            }),
      conditions: queryVariables?.conditions || null,
      first: queryVariables?.first || null,
      after: queryVariables?.after || null,
      before: queryVariables?.before || null,
      orderBy: queryVariables?.orderBy || null,
      last: queryVariables?.last || null,
    };
  };

  // Generates query for every aggregate definition
  const queries = useMemo(
    () => createWithAggregateNameObject(mapAggregateDefinitions, definition => QUERY(definition)),
    [mapAggregateDefinitions]
  );
  const recordTypeCountQueries = useMemo(
    () => createWithAggregateNameObject(mapAggregateDefinitions, definition => RecordTypeAssetCountQuery(definition)),
    [mapAggregateDefinitions]
  );

  const getFilterValueForArray = (
    value: Array<number | string | null>,
    filterKey: string,
    isRelationship?: boolean,
    not?: boolean
  ) => {
    const nonNullArray = Array.isArray(value) ? value.filter(val => val !== null) : [];
    const valueArray: Array<Record<string, ValueRecord> | ValueRecord> = [];
    if (isRelationship) {
      if (
        ((value as Array<string | null>).includes(null) && !not) ||
        (!(value as Array<string | null>).includes(null) && not)
      ) {
        valueArray.push({
          [`${filterKey}Exists`]: false,
        });
      }
      if ((value as Array<string | null>).includes(null) && !nonNullArray.length && not) {
        valueArray.push({
          [`${filterKey}Exists`]: true,
        });
      }
      if (nonNullArray.length || !not) {
        valueArray.push({
          [filterKey]: { label: { [not ? 'notInInsensitive' : 'inInsensitive']: nonNullArray } },
        });
      }
    } else {
      if (
        ((value as Array<string | null>).includes(null) && !not) ||
        (!(value as Array<string | null>).includes(null) && not)
      ) {
        valueArray.push({
          [filterKey]: { isNull: true },
        });
      }
      if ((value as Array<string | null>).includes(null) && !nonNullArray.length && not) {
        valueArray.push({
          [filterKey]: { isNull: false },
        });
      }
      if (nonNullArray.length) {
        valueArray.push({
          [filterKey]: { [not ? 'notIn' : 'in']: nonNullArray },
        });
      }
    }
    return valueArray;
  };

  const getFilterValue = (
    filterKey: string,
    value: string | string[] | { not: (string | null)[] },
    fieldType?: string,
    isRelationship?: boolean,
    field?: string
  ) => {
    if (isRelationship && field) {
      let constraintObject = null;
      if (Array.isArray(value) || typeof value === 'object') {
        const arr = Array.isArray(value) ? value : value.not;
        return getFilterValueForArray(arr, filterKey, isRelationship, !Array.isArray(value));
      } else {
        return (constraintObject = { label: { includesInsensitive: value as string } });
      }
    }

    if (Array.isArray(value)) {
      if ((value as Array<string | null>).includes(null)) {
        return getFilterValueForArray(value, filterKey, isRelationship);
      }
      return { in: value };
    }

    if (typeof value === 'object') {
      return getFilterValueForArray(value.not, filterKey, isRelationship, true);
    }
    switch (fieldType) {
      case 'String':
        return { includesInsensitive: value };
      case 'Boolean':
        const options = [];
        if ('true'.includes(value.toLowerCase())) {
          options.push(true);
        }
        if ('false'.includes(value.toLowerCase())) {
          options.push(false);
        }
        return { in: options };
      default:
        return { includesInsensitive: value };
    }
  };

  const getCondition = () => {
    const dateTimeCondition: Record<string, string> = {};
    const intAndFloatCondition: Record<string, string> = {};
    const result: Conditions = {};
    if (filterValues.length) {
      filteredSearchKeys.forEach((item, index) => {
        const key = item.split('-')[0];
        const fieldDef = selectedRecordTypeDefinition?.propertyDefinitions.find(def => def.field === key);
        const filterKey = fieldDef?.field;
        const filterValue = filterValues[index];
        if (fieldDef?.type === 'DateTime' && filterKey && item.includes('-')) {
          dateTimeCondition[filterKey] = filterValue;
        } else if (
          (fieldDef?.type === 'Float' || fieldDef?.type === 'Int') &&
          filterKey &&
          !Array.isArray(filterValue) &&
          !filterValue?.not
        ) {
          intAndFloatCondition[filterKey] = filterValue;
        }
      });
    }

    if (Object.keys(dateTimeCondition).length) {
      result.dateTimeStartsWith = { timezoneDifference: getTimeZoneDifference(), filters: dateTimeCondition };
    }

    if (Object.keys(intAndFloatCondition).length) {
      result.numberContains = intAndFloatCondition;
    }

    if (selectedRecordTypeDefinition && aiFilter[selectedRecordTypeDefinition.name]) {
      result.aiSearch = aiFilter[selectedRecordTypeDefinition.name];
    }
    return result;
  };

  const getFilters = (isReferenceType: boolean, recordDefinition?: AggregateDefinition): any => {
    const def = recordDefinition || selectedRecordTypeDefinition;
    const filters: Filters = isReferenceType
      ? { label: { notEqualTo: '' } }
      : {
          projectId: projectsFilter,
        };

    const ands: Filters['and'] = [];
    if (def && !isEmpty(quickSearch)) {
      filters['fullText'] = { includesInsensitive: quickSearch };
    }

    if (drawFilter.enabled && !_.isEmpty(drawFilter.features)) {
      const ors = drawFilter.features.map(f => ({
        primaryLocation: { coveredBy: { type: f.type, coordinates: [[...f.coordinates[0], f.coordinates[0][0]]] } },
      }));
      ands.push({ or: ors });
      if (configContext.integrations?.floorPlans && def?.propertyDefinitions.some(d => d.field === 'floorPlan')) {
        const floorPlanId = selectedFloorPlanRef.current?.id;
        filters['floorPlanId'] = floorPlanId
          ? {
              equalTo: floorPlanId,
            }
          : {
              isNull: true,
            };
      }
    }

    if (isMapExtentEnabledRef.current) {
      const [worldMinLon, worldMinLat, worldMaxLon, worldMaxLat] = [-180, -90, 180, 90];
      const regionMinLat = Math.max(mapViewBoxRef.current?.minLat ?? worldMinLat, worldMinLat);
      const regionMaxLat = Math.min(mapViewBoxRef.current?.maxLat ?? worldMaxLat, worldMaxLat);
      const regionMinLon = Math.max(mapViewBoxRef.current?.minLon ?? worldMinLon, worldMinLon);
      const regionMaxLon = Math.min(mapViewBoxRef.current?.maxLon ?? worldMaxLon, worldMaxLon);
      const polygonSections: Array<GeoJSON.Polygon['coordinates'][number]> = [];
      const splitDegrees = 45;
      const [xSplit, ySplit] = [
        Math.ceil((regionMaxLon - regionMinLon) / splitDegrees),
        Math.ceil((regionMaxLat - regionMinLat) / splitDegrees),
      ];
      for (let x = 0; x < xSplit; x++) {
        for (let y = 0; y < ySplit; y++) {
          const x1 = regionMinLon + (x * (regionMaxLon - regionMinLon)) / xSplit;
          const y1 = regionMinLat + (y * (regionMaxLat - regionMinLat)) / ySplit;
          const x2 = regionMinLon + ((x + 1) * (regionMaxLon - regionMinLon)) / xSplit;
          const y2 = regionMinLat + ((y + 1) * (regionMaxLat - regionMinLat)) / ySplit;
          polygonSections.push([
            [x1, y1],
            [x2, y1],
            [x2, y2],
            [x1, y2],
            [x1, y1],
          ]);
        }
      }

      ands.push({
        or: polygonSections.map(ps => ({ primaryLocation: { coveredBy: { type: 'Polygon', coordinates: [ps] } } })),
      });

      if (configContext.integrations?.floorPlans && def?.propertyDefinitions.some(d => d.field === 'floorPlan')) {
        const floorPlanId = selectedFloorPlanRef.current?.id;
        filters['floorPlanId'] = floorPlanId
          ? {
              equalTo: floorPlanId,
            }
          : {
              isNull: true,
            };
      }
    }

    const recordFilterState = def?.name ? getRecordFilter(def.name) : filterState;

    if (!isEmpty(recordFilterState)) {
      Object.entries(recordFilterState).forEach(([item, value]) => {
        const key = item.split('-')[0];
        const fieldDef = def?.propertyDefinitions.find(def => def.field === key);
        const filterKey = key;
        if (fieldDef?.type !== 'DateTime' && fieldDef?.type !== 'Float' && fieldDef?.type !== 'Int') {
          const filterValue = getFilterValue(
            filterKey,
            value,
            fieldDef?.type,
            fieldDef?.isRelationship,
            fieldDef?.field
          );
          if (Array.isArray(filterValue)) {
            ands.push({ or: filterValue as FilterValue });
          } else {
            filters[filterKey] = { ...filters[filterKey], ...filterValue };
          }
        } else if ((fieldDef?.type === 'Float' || fieldDef?.type === 'Int') && (Array.isArray(value) || value?.not)) {
          let hasNull = false;
          const values: any[] = value?.not ?? value;
          const arrayOperator = value?.not ? 'notIn' : 'in';
          const convertedArray = values.reduce((acc, strValue) => {
            if (strValue === null) {
              hasNull = true;
            } else {
              const numericValue = Number(strValue);
              acc.push(isNaN(numericValue) ? strValue : numericValue);
            }
            return acc;
          }, []);
          if ((hasNull && arrayOperator === 'in') || (!hasNull && arrayOperator === 'notIn')) {
            ands.push({
              or: [{ [filterKey]: { isNull: true } }, { [filterKey]: { [arrayOperator]: convertedArray } }],
            });
          } else if (hasNull && arrayOperator === 'notIn' && convertedArray.length === 0) {
            filters[filterKey] = { ...filters[filterKey], isNull: false };
          } else {
            filters[filterKey] = { ...filters[filterKey], [arrayOperator]: convertedArray };
          }
        } else if (fieldDef?.type === 'DateTime' && Array.isArray(value) && !item.includes('-')) {
          const startTime = new Date(value[0]).toJSON();
          const endTime = new Date(value[1]).toJSON();
          filters[filterKey] = { ...filters[filterKey], greaterThanOrEqualTo: startTime, lessThanOrEqualTo: endTime };
        }
      });
    }
    if (ands.length) {
      filters['and'] = ands;
    }
    return filters;
  };

  const fetchRecordTypeCount = async () => {
    const queries = Object.keys(recordTypeCountQueries).map(
      (key: string): Promise<{ key: string; result: ApolloQueryResult<recordTypeCountResult> }> => {
        const isReferenceType = mapAggregateDefinitions.find(def => def.queryKey === key)?.isReferenceType;
        const queryVariable = getQueryVariable(undefined, isReferenceType);
        return client
          .query({
            fetchPolicy: 'no-cache',
            query: recordTypeCountQueries[key],
            variables: queryVariable,
          })
          .then(result => {
            return { key, result };
          })
          .catch(e => {
            return { key, result: e };
          });
      }
    );
    Promise.all(queries).then(results => {
      let recordData: record[] = [];
      results.map(recordType => {
        const data = recordType.result.data as unknown as recordTypeCountResult['data'];
        const record = mapAggregateDefinitions.find(
          mapAggregateDefinitions => mapAggregateDefinitions.queryKey === recordType.key
        );
        if (record && data && data[record.queryKey].totalCount !== 0) {
          let obj = {
            value: record.name,
            label: `${translate(record.plural)} (${data[record.queryKey].totalCount})`,
            plural: record.plural,
            count: data[record.queryKey].totalCount,
          };
          recordData.push(obj);
        }
      });
      setRecordTypesData(recordData);
    });
  };

  useEffect(() => {
    fetchRecordTypeCount();
    fetchReferenceTypeAggregates();
  }, [projectsFilter, language]);

  useEffect(() => {
    if (configContext.integrations?.floorPlans) {
      fetchBuildingsAndFloorPlans();
    }
  }, [mapAggregateDefinitions, projectsFilter]);

  useEffect(() => {
    if (!firstLoad) {
      initialAssetsFetch(false, true);
    }
  }, [isMapExtentEnabled, drawFilter, projectsFilter, aiFilter]);

  useEffect(() => {
    if (!firstLoad) {
      if (isMapExtentEnabled) {
        initialAssetsFetch(false, true);
      }
    }
  }, [mapViewBox]);

  useEffect(() => {
    if (!firstLoad) {
      initialAssetsFetch(true);
    }
  }, [quickSearch, searchKey, sort]);

  useEffect(() => {
    initialAssetsFetch(true);
    setFirstLoad(false);
  }, []);

  const { refetch, fetchRecordData } = useLazyQueries<QueryResultDataType>(
    queries,
    {
      fetchPolicy: 'no-cache',
    },
    {}
  );

  const columnSorting = ({ sortDirection, sortColumn }: { sortColumn?: string; sortDirection?: SortDirection }) => {
    let sortKey = undefined;
    const fieldDef = selectedRecordTypeDefinition?.propertyDefinitions.find(def => def.field === sortColumn);
    if (sortColumn && sortDirection !== 'NONE') {
      const pascal = getSortKey(pascalCase(sortColumn)).toUpperCase();
      if (fieldDef?.isRelationship) {
        const fieldTypePascal = getSortKey(pascalCase(fieldDef.type)).toUpperCase();
        if (fieldDef?.relationshipType !== 'ONE_TO_MANY')
          sortKey = `${fieldTypePascal}_BY_${pascal}_ID__LABEL_${sortDirection}`;
        else {
          const foreignColumn = getSortKey(pascalCase(fieldDef.foreignColumn || '')).toUpperCase();
          sortKey = `${fieldTypePascal}S_BY_${foreignColumn}_ID__MIN_LABEL_${sortDirection}`;
        }
      } else if (['PhotoCollection', 'FileCollection'].some(x => x === fieldDef?.type)) {
        sortKey = ''; // Note: [S5-3801] Disable sort for complex columns until PG update. https://stackoverflow.com/a/16611128
      } else {
        sortKey = `${pascal}_${sortDirection}`;
      }
    } else if (sortColumn !== '') {
      sortKey = '';
    }
    return sortKey;
  };

  const fetchReferenceTypeAggregates = async () => {
    const referenceTypeAggregatesDef = mapAggregateDefinitions.filter(def => def.isReferenceType);
    const referenceTypeVariable = {
      filters: { label: { notEqualTo: '' } },
    };
    const allQueries = referenceTypeAggregatesDef.map(record => {
      const queryKey = record.queryKey;
      return client
        .query({
          fetchPolicy: 'no-cache',
          query: queries[queryKey],
          variables: referenceTypeVariable,
        })
        .then(result => {
          return { queryKey, result };
        })
        .catch(e => {
          return { queryKey, result: e };
        });
    });
    Promise.all(allQueries).then(results => {
      let assetData: MapAssetType[] = [];
      results.map(result => {
        const querykey = result.queryKey;
        if (result.result.data && result.result.data[querykey]) {
          const modifiedData = extractDataFromResult(result.result.data[querykey], querykey);
          assetData.push(...modifiedData[querykey].assetData);
        }
      });
      setReferenceTypeAssets(assetData);
    });
  };

  const fetchBuildingsAndFloorPlans = async () => {
    const targetKeys = [BUILDING_DEFAULT_KEYS.recordTypeKey as string, FLOOR_PLAN_DEFAULT_KEYS.recordTypeKey as string];
    const definitions = mapAggregateDefinitions.filter(def => targetKeys.includes(def.queryKey));
    const allQueries = definitions.map(def => {
      const query = queries[def.queryKey];
      const variables: queryVariables = {
        filters: { projectId: projectsFilter },
      };
      return client
        .query({ query, variables, fetchPolicy: 'no-cache' })
        .then(result => ({ queryKey: def.queryKey, result: result.data }))
        .catch(error => ({ queryKey: def.queryKey, result: error }));
    });

    Promise.all(allQueries).then(results => {
      let allBuildings: BuildingType[] = [];
      let allFloorPlans: FloorPlanType[] = [];
      results.map(result => {
        const querykey = result.queryKey;
        if (result.result && result.result[querykey]) {
          const modifiedData = extractDataFromResult(result.result[querykey], querykey);
          if (querykey === BUILDING_DEFAULT_KEYS.recordTypeKey) {
            allBuildings = modifiedData[querykey].assetData as BuildingType[];
          } else if (querykey === FLOOR_PLAN_DEFAULT_KEYS.recordTypeKey) {
            allFloorPlans = modifiedData[querykey].assetData as FloorPlanType[];
          }
        }
      });

      setBuildings(allBuildings);
      setFloorPlans(allFloorPlans);
    });
  };

  const getQueryKey = (connectionString: string) => {
    const suffix = 'Connection';
    if (connectionString.endsWith(suffix)) {
      return connectionString.slice(0, -suffix.length);
    }
    return connectionString;
  };

  const getSearchResults = async (searchText: string) => {
    try {
      const validDefinitions = mapAggregateDefinitions.filter(def =>
        recordTypesData?.some(data => data.value === def.name && data.count > 0)
      );
      const visibleAggregateDefinitions = validDefinitions.filter(def => visibleAggregateTypesNames.includes(def.name));
      const query = GlobalSearchQueryCreator(visibleAggregateDefinitions, selectedProjects, searchText);
      const fetchedData = await client.query({
        fetchPolicy: 'no-cache',
        query: query,
        errorPolicy: 'ignore',
      });
      const result: MapAssetType[] = [];
      Object.keys(fetchedData.data).map((aggregateType: string) => {
        result.push(...getAssetsFromResults(fetchedData.data[aggregateType].edges, getQueryKey(aggregateType)));
      });
      return result;
    } catch (e) {
      console.error('Cross Origin Error', e);
      return [];
    }
  };

  const initialAssetsFetch = async (checkExsistingData: boolean, clearExistingData?: boolean) => {
    if (selectedProjects.length === 0) {
      setIsDataLoading(false);
      setAssetStates(createWithAggregateNameObject(mapAggregateDefinitions, () => ({})));
      return 0;
    }
    setIsDataLoading(true);
    const querykey: string | undefined = selectedRecordTypeDefinition?.queryKey;
    if (querykey) {
      const sortKey = columnSorting({ sortColumn: sort?.sortKey, sortDirection: sort?.sortDirection });
      const variables: queryVariables = {
        filters: getFilters(!!selectedRecordTypeDefinition!.isReferenceType),
        conditions: getCondition(),
        first: INITIAL_CHUNK_SIZE,
        orderBy: sortKey,
      };
      if (sortKey !== undefined) {
        const isSameVariables = isEqual(assetQueryState[querykey]?.variables, variables);
        const queryVariable = getQueryVariable(variables, !!selectedRecordTypeDefinition!.isReferenceType);
        const existingData = assetStates[querykey] as recordData;
        const isSameSort = assetQueryState[querykey]?.sortKey === sortKey;
        const columnKey = sortKey !== '' ? sortKey.split(/(_ASC|_DESC)/)[0] : '';
        const storedColumnKey = assetQueryState[querykey]?.columnKey;
        const doesDataExists = checkForExistingData(checkExsistingData, existingData);
        setInitialTableLoadState(querykey !== prevQueryKey.current);
        if (!doesDataExists || !(isSameSort && isSameVariables)) {
          const data = await fetchRecordData(queries[querykey], queryVariable as unknown as VariablesRecord, querykey);
          if (data === undefined) {
            return;
          }
          if (!_.isEmpty(data)) {
            const modifiedData = extractDataFromResult((data as fetchedResult)[querykey].data, querykey);
            setAssetStates(prev => ({
              ...(clearExistingData ? {} : prev),
              [querykey]: modifiedData[querykey],
            }));
            setAssetQueryState(prev => ({
              ...prev,
              [querykey]: {
                sortKey,
                columnKey,
                variables,
              },
            }));
            Object.keys(modifiedData).forEach(key => {
              setFilteredCount(prev => ({
                ...prev,
                [selectedRecordType]: modifiedData[key].totalCount,
              }));
            });
          } else {
            const isSameColumn = storedColumnKey === columnKey;
            if (isSameColumn) {
              setAssetStates(prev => ({
                ...(clearExistingData ? {} : prev),
                [querykey]: {},
              }));
              setAssetQueryState(prev => ({
                ...(clearExistingData ? {} : prev),
                [querykey]: { ...prev.querykey, sortKey: '' },
              }));
            }
          }
          setIsDataLoading(false);
        } else {
          setIsDataLoading(false);
        }
        setInitialTableLoadState(false);
        prevQueryKey.current = querykey;
      }
    }
  };

  const fetchNextDataSet = async (fetchAfter: boolean) => {
    if (isMapExtentEnabled || (drawFilter.enabled && !_.isEmpty(drawFilter.features))) return;
    const querykey: string | undefined = mapAggregateDefinitions.find(def => def.name === selectedRecordType)?.queryKey;
    if (querykey) {
      const currentData = assetStates[querykey] as recordData;
      const hasFetchableData = () => {
        if (!fetchAfter) {
          if (currentData.pageInfo?.hasPreviousPage) {
            return currentData.pageInfo?.hasPreviousPage;
          }
        } else if (currentData.pageInfo?.hasNextPage) {
          return currentData.pageInfo?.hasNextPage;
        }
        return false;
      };

      if (currentData?.assetData && currentData.pageInfo && hasFetchableData()) {
        const startCursor = currentData?.assetData[0].cursor as string;
        const endCursor = _.last(currentData.assetData)?.cursor as string;
        const variable: queryVariables = {
          filters: getFilters(!!selectedRecordTypeDefinition?.isReferenceType),
          conditions: getCondition(),
          first: fetchAfter ? NEXT_CHUNK_SIZE : undefined,
          after: fetchAfter ? endCursor : undefined,
          orderBy: assetQueryState[querykey]?.sortKey,
          before: fetchAfter ? undefined : startCursor,
          last: fetchAfter ? undefined : NEXT_CHUNK_SIZE,
        };
        const queryVariable = getQueryVariable(variable, !!selectedRecordTypeDefinition?.isReferenceType);
        const data = await fetchRecordData(queries[querykey], queryVariable as unknown as VariablesRecord, querykey);
        if (data === undefined) {
          return;
        }
        const modifiedData = extractDataFromResult((data as fetchedResult)[querykey].data, querykey);
        const fetchedAsset = modifiedData[querykey!].assetData;
        let newData = [...(currentData.assetData as MapAssetType[])];
        if (fetchAfter) {
          newData.splice(0, fetchedAsset.length);
          newData.push(...fetchedAsset);
        } else {
          newData.splice(newData.length - fetchedAsset.length, newData.length);
          newData.unshift(...fetchedAsset);
        }
        setAssetStates(prev => ({
          ...prev,
          [querykey]: {
            assetData: newData,
            pageInfo: modifiedData[querykey].pageInfo,
          },
        }));
        Object.keys(modifiedData).forEach(key => {
          setFilteredCount(prev => ({
            ...prev,
            [selectedRecordType]: modifiedData[key].totalCount,
          }));
        });
        return fetchedAsset.length;
      }
      return 0;
    }
  };

  const updateScrollPosition = (recordType: string, index: number) => {
    const recordKey: string | undefined = mapAggregateDefinitions.find(def => def.name === recordType)?.queryKey;
    if (recordKey) {
      setAssetStates(prev =>
        prev[recordKey]
          ? {
              ...prev,
              [recordKey]: {
                assetData: prev[recordKey].assetData,
                pageInfo: prev[recordKey].pageInfo,
                scrollInfo: index,
              },
            }
          : prev
      );
    }
  };

  const evaluateFilter = (filter: any, item: any, key: string): boolean => {
    let included = !isNil(item);
    for (const k of Object.keys(filter)) {
      switch (k) {
        case 'in':
          included =
            included &&
            filter[k].some((i: string | number) => (String(item[key]) || '').toLowerCase() === String(i).toLowerCase());
          break;
        case 'notIn':
          included =
            included &&
            !filter[k].some(
              (i: string | number) => (String(item[key]) || '').toLowerCase() === String(i).toLowerCase()
            );
          break;
        case 'inInsensitive':
          included =
            included && filter[k].some((i: string) => ((item[key] as string) || '').toLowerCase() === i.toLowerCase());
          break;
        case 'includesInsensitive':
          included = included && ((item[key] as string) || '').toLowerCase().includes(filter[k].toLowerCase());
          break;
        case 'greaterThan':
          included = included && item[key] > filter[k];
          break;
        case 'lessThan':
          included = included && item[key] < filter[k];
          break;
        case 'greaterThanOrEqualTo':
          included = included && item[key] >= filter[k];
          break;
        case 'lessThanOrEqualTo':
          included = included && item[key] <= filter[k];
          break;
        case 'notEqualTo':
          included = included && item[key] !== filter[k];
          break;
        case 'equalTo':
          included = included && item[key] === filter[k];
          break;
        case 'isNull':
          included = included && (item[key] === null || item[key] === undefined);
          break;
        case 'label':
          included = included && evaluateFilter(filter[k], item[key], 'label');
          break;
        case 'coveredBy':
          try {
            const turfPoly = polygon(filter[k].coordinates);
            included = included && inside(point(item[key].coordinates), turfPoly);
          } catch (e) {
            console.log('Error in coveredBy filter', e);
          }
          break;
        default:
          console.log('Missing filter logic!', k);
          included = false;
      }
      if (!included) break;
    }
    return included;
  };

  const checkFilters = (filters: any, item: MapAssetType) => {
    let included = true;
    for (const k of Object.keys(filters)) {
      switch (k) {
        case 'and':
          included = included && filters[k].every((i: any) => checkFilters(i, item));
          break;
        case 'or':
          included = included && filters[k].some((i: any) => checkFilters(i, item));
          break;
        default:
          included = included && evaluateFilter(filters[k], item, k);
      }
    }
    return included;
  };

  const sortAssets = (
    assets: MapAssetType[],
    sortKey: string = sortRef.current?.sortKey,
    sortDirection: SortDirection = sortRef.current?.sortDirection
  ) => {
    const fieldDef = selectedRecordTypeDefinitionRef.current?.propertyDefinitions.find(def => def.field === sortKey);

    if (!sortDirection || sortDirection === 'NONE' || !sortKey) return assets;

    const sorted = [...assets].sort((a, b) => {
      const getValue = (item: MapAssetType) => {
        if (fieldDef?.isRelationship) {
          if (fieldDef?.relationshipType === 'ONE_TO_MANY') {
            return (
              (item[sortKey] as Array<{ id: string; label: string }>)?.sort((a, b) =>
                (a?.label || '').localeCompare(b?.label || '')
              )[0]?.label || ''
            );
          }
          return ((item[sortKey] as { id: string; label: string })?.label || '').toString();
        }
        return item[sortKey] ? item[sortKey].toString() : '';
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (!aValue && bValue) return 1;
      if (aValue && !bValue) return -1;
      if (aValue === bValue) return a.id.localeCompare(b.id); // This won't necessarily match DB order, but will produce consistent results.
      return aValue.localeCompare(bValue);
    });

    return sortDirection === 'DESC' ? sorted.reverse() : sorted;
  };

  const updateAssetStates = (recordType: string, modifiedAssetData: MapAssetType[]) => {
    const regionMinLat = mapViewBoxRef.current?.minLat || -200;
    const regionMaxLat = mapViewBoxRef.current?.maxLat || 200;
    const regionMinLon = mapViewBoxRef.current?.minLon || -200;
    const regionMaxLon = mapViewBoxRef.current?.maxLon || 200;
    if (
      modifiedAssetData.find(a => {
        if (!mapViewBoxRef.current) {
          return true;
        }
        if (a.primaryLocation.type === 'Point') {
          return (
            regionMinLat < a.primaryLocation.coordinates[1] &&
            regionMaxLat > a.primaryLocation.coordinates[1] &&
            regionMinLon < a.primaryLocation.coordinates[0] &&
            regionMaxLon > a.primaryLocation.coordinates[0]
          );
        }
        const minLat = Math.min(...a.primaryLocation.coordinates.map(c => c[1])),
          minLong = Math.min(...a.primaryLocation.coordinates.map(c => c[0])),
          maxLat = Math.max(...a.primaryLocation.coordinates.map(c => c[1])),
          maxLong = Math.max(...a.primaryLocation.coordinates.map(c => c[0]));
        return regionMinLat < maxLat && regionMaxLat > minLat && regionMinLon < maxLong && regionMaxLon > minLong;
      })
    ) {
      setForceMapReload(true);
    }
    if (modifiedAssetData.find(a => cardIdsRef.current.includes(a.id))) {
      setRefresh(true);
    }
    const recordDefinition = mapAggregateDefinitions.find(d => d.queryKey === recordType);
    const recordFilters = getFilters(recordDefinition?.isReferenceType || false, recordDefinition);
    const filteredData = modifiedAssetData.filter(d => checkFilters(recordFilters, d));
    setAssetStates(prev => {
      if (!prev[recordType]) {
        return prev;
      }
      let assetData: MapAssetType[] = [];
      const tempAssetData = prev[recordType].assetData;
      if (Array.isArray(tempAssetData)) {
        assetData = [...tempAssetData];
      } else if (typeof tempAssetData === 'object' && tempAssetData !== undefined) {
        assetData = Object.values(tempAssetData);
      }
      const updatedData = assetData
        .map(a => {
          const newData = filteredData.find(d => d.id === a.id);
          return newData || a;
        })
        .filter(d => checkFilters(recordFilters, d))
        .concat(filteredData.filter(d => !assetData.find(a => a.id === d.id)));
      return {
        ...prev,
        [recordType]: {
          assetData: sortAssets(updatedData),
          pageInfo: prev[recordType].pageInfo,
          scrollInfo: prev[recordType].scrollInfo,
        },
      };
    });
    fetchRecordTypeCount();
  };

  const update = async (recordType: string[], updatedProject: string) => {
    if (recordType.some(type => NOT_MAP_ASSETS_NAMES.some(name => type === name))) {
      refetchProjects();
    }
    if (!selectedProjects.includes(updatedProject)) {
      return;
    }
    if (recordType.some(type => BUILDING_DEFAULT_KEYS.typeName === type || FLOOR_PLAN_DEFAULT_KEYS.typeName === type)) {
      fetchBuildingsAndFloorPlans();
    }
    const currentTime = Date.now() - 30 * 1000;
    const sinceTime = new Date(currentTime).toJSON();
    let allQueries: Record<string, DocumentNode> = {};
    recordType.map((record: string) => {
      const key = mapAggregateDefinitions.find(def => def.name === record)?.queryKey;
      if (key) {
        allQueries[key] = queries[key];
      }
    });
    const queryVariable = {
      filters: { projectId: projectsFilter, lastModelRefresh: { greaterThan: sinceTime } },
    };
    if (_.isEmpty(allQueries)) return;
    try {
      const data = await refetch(queryVariable, allQueries);
      Object.keys(data as fetchedResult).map((recordType: string) => {
        const modifiedData = extractDataFromResult((data as fetchedResult)[recordType].data, recordType);
        updateAssetStates(recordType, modifiedData[recordType].assetData);
      });
    } catch (error) {
      console.error('Error while fetching updated data', error);
    }
  };

  const syncUnavailableData = async (sinceTime: number, endTime: number) => {
    const limit = 2000;
    let moreData = true;
    try {
      while (moreData) {
        const query = UnavailableDataQuery(sinceTime, endTime, limit);
        const fetchedData = await client.query({
          fetchPolicy: 'no-cache',
          query: query,
        });
        if (fetchedData.data?.unavailableIds?.length) {
          await deleteAssetsById(fetchedData.data.unavailableIds.map((data: { id: string }) => data.id));
        }
        moreData = fetchedData.data?.unavailableIds?.length === limit;
      }
      return;
    } catch (err) {
      console.error('Unavailable data error', err);
    }
  };

  const deleteAssets = (deletedAssets: Record<string, string[]>) => {
    const deletedAssetTypes = Object.keys(deletedAssets);
    const allIds = deletedAssetTypes.reduce<string[]>((prev, recordType: string) => {
      return prev.concat(...deletedAssets[recordType]);
    }, []);
    if (cardIdsRef.current.find(id => allIds.includes(id))) {
      setRefresh(true);
    }
    setForceMapReload(true);
    deletedAssetTypes.map((recordType: string) => {
      const aggregateDefinition = mapAggregateDefinitions.find(def => def.name === recordType);
      if (aggregateDefinition?.queryKey) {
        setAssetStates(prev => {
          if (prev[aggregateDefinition.queryKey]?.assetData) {
            const assetData = [...(prev[aggregateDefinition.queryKey].assetData as MapAssetType[])];
            const modifiedData = assetData.filter(asset => !deletedAssets[recordType].includes(asset.id));
            return {
              ...prev,
              [aggregateDefinition.queryKey]: {
                assetData: modifiedData,
                pageInfo: prev[aggregateDefinition.queryKey].pageInfo,
                scrollInfo: prev[aggregateDefinition.queryKey].scrollInfo,
              },
            };
          }
          return prev;
        });
      }
    });
    fetchRecordTypeCount();
    if (deletedAssetTypes.some(type => NOT_MAP_ASSETS_NAMES.some(name => type === name))) {
      refetchProjects();
    }
    if (
      deletedAssetTypes.some(
        type => BUILDING_DEFAULT_KEYS.typeName === type || FLOOR_PLAN_DEFAULT_KEYS.typeName === type
      )
    ) {
      fetchBuildingsAndFloorPlans();
    }
  };

  const deleteAssetsById = (allIds: string[]) => {
    if (cardIdsRef.current.find(id => allIds.includes(id))) {
      setRefresh(true);
    }
    setForceMapReload(true);
    for (const aggregateDefinition of mapAggregateDefinitions) {
      if (aggregateDefinition?.queryKey) {
        setAssetStates(prev => {
          if (prev[aggregateDefinition.queryKey]?.assetData) {
            const assetData = [...(prev[aggregateDefinition.queryKey].assetData as MapAssetType[])];
            const modifiedData = assetData.filter(asset => !allIds.includes(asset.id));
            return {
              ...prev,
              [aggregateDefinition.queryKey]: {
                assetData: modifiedData,
                pageInfo: prev[aggregateDefinition.queryKey].pageInfo,
                scrollInfo: prev[aggregateDefinition.queryKey].scrollInfo,
              },
            };
          }
          return prev;
        });
      }
    }
    fetchRecordTypeCount();
  };

  const fetchAssetsForFilter = async (field: string, limit?: number, offset?: number, searchText?: string) => {
    if (selectedRecordTypeDefinition) {
      const queryVariable = {
        selectedProjects,
      };
      const selectedField = selectedRecordTypeDefinition.propertyDefinitions.find(def => def.field === field);
      const query = FilterQuery(
        selectedRecordTypeDefinition,
        aggregateDefinitions,
        field,
        selectedField?.type || '',
        limit,
        offset,
        searchText
      );
      try {
        const fetchedData = await client.query({
          fetchPolicy: 'no-cache',
          query: query,
          variables: queryVariable,
        });

        if (_.isEmpty(fetchedData)) {
          return [];
        }
        return fetchedData.data.distinctValues.map((value: Record<string, string>) =>
          selectedField?.isRelationship ? value.label : value[field]
        );
      } catch (err) {
        console.error('Filter error', err);
        return [];
      }
    }
  };

  const refetchAll = () => {
    setAssetStates(createWithAggregateNameObject(mapAggregateDefinitions, () => ({})));
    initialAssetsFetch(false);
  };

  const fetchNextForMultiSelect = async (cursor: string | undefined, pageSize?: number) => {
    const variables: queryVariables = {
      filters: getFilters(!!selectedRecordTypeDefinition?.isReferenceType),
      conditions: getCondition(),
      after: cursor,
      first: pageSize || PAGE_SIZE_MULTISELECT,
    };
    const queryVariable = getQueryVariable(variables, !!selectedRecordTypeDefinition?.isReferenceType);
    const querykey: string | undefined = selectedRecordTypeDefinition?.queryKey;
    if (querykey) {
      const data = await fetchRecordData(queries[querykey], queryVariable as unknown as VariablesRecord, querykey);
      if (!_.isEmpty(data)) {
        const modifiedData = extractDataFromResult((data as fetchedResult)[querykey].data, querykey);
        Object.keys(modifiedData).forEach(key => {
          setFilteredCount(prev => ({
            ...prev,
            [selectedRecordType]: modifiedData[key].totalCount,
          }));
        });
        let { hasNextPage } = (data as fetchedResult)[querykey].data.pageInfo;
        let cursor = _.last(modifiedData[querykey].assetData)?.cursor;
        return {
          nextHasNextPage: hasNextPage,
          nextCursor: cursor,
          moreData: modifiedData[querykey].assetData as AssetType[],
        };
      }
    }
    return { nextHasNextPage: false, nextCursor: '', moreData: [] };
  };

  const getAssetsForMultiSelect = async () => {
    if (selectedProjects.length === 0) {
      return [];
    }

    let {
      moreData: result,
      nextHasNextPage: hasNextPage,
      nextCursor: cursor,
    } = await fetchNextForMultiSelect(undefined, configContext.multiselectPageSize);
    while (hasNextPage && cursor && result.length < MAX_ASSET_LIMIT_MULTISELECT) {
      const { moreData, nextHasNextPage, nextCursor } = await fetchNextForMultiSelect(
        cursor,
        configContext.multiselectPageSize
      );
      result = result.concat(...moreData);
      hasNextPage = nextHasNextPage;
      cursor = nextCursor;
    }
    return result;
  };

  return {
    refetchAll,
    isDataLoading,
    assets: selectedRecordTypeAssets,
    referenceTypeAssets,
    recordTypesData,
    fetchNextDataSet,
    updateScrollPosition,
    update,
    syncUnavailableData,
    deleteAssets,
    deleteAssetsById,
    fetchAssetsForFilter,
    filteredCount,
    getSearchResults,
    getAssetsForMultiSelect,
    sortAssets,
  };
};
